import React, { useState, useRef } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import ProjectAPIs from '../../utilities/api/project/AdminProjectAPI';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';

// CSV Import Schema Constants
const projectSchemaFields = [
  'title',
  'description',
  'student_email',
  'type',
  'progress_status',
  'teammates_emails',
  'documents',
  'links',
  'platform',
  'expenditure',
  'budget',
  'duration',
  'is_patent_filed',
  'mentors',
];

const projectSchemaDescription = {
  title: { desc: 'Title of the project', mandatory: true, options: 'Text' },
  description: {
    desc: 'Description of the project',
    mandatory: true,
    options: 'Text',
  },
  student_email: {
    desc: 'Email address of the student (project owner)',
    mandatory: true,
    options: 'Email string',
  },
  type: {
    desc: 'Type of the project',
    mandatory: true,
    options: 'Academic, Research, Personal, Other',
  },
  progress_status: {
    desc: 'Progress status of the project',
    mandatory: true,
    options: 'Not Started, In Progress, Completed',
  },
  teammates_emails: {
    desc: 'Comma-separated email addresses of teammates',
    mandatory: false,
    options: 'Email strings',
  },
  documents: {
    desc: 'Link to documents (URL)',
    mandatory: false,
    options: 'URL string',
  },
  links: {
    desc: 'Comma-separated relevant URLs',
    mandatory: false,
    options: 'URL strings',
  },
  platform: {
    desc: 'Platform for the project',
    mandatory: false,
    options: 'Competitions, Exhibitions, Industry',
  },
  expenditure: {
    desc: 'Amount spent (number)',
    mandatory: false,
    options: 'Number',
  },
  budget: {
    desc: 'Project budget (number)',
    mandatory: false,
    options: 'Number',
  },
  duration: {
    desc: 'Duration of the project in months (number)',
    mandatory: false,
    options: 'Number (e.g., 3.5)',
  },
  is_patent_filed: {
    desc: 'Is a patent filed? (TRUE/FALSE)',
    mandatory: true,
    options: 'TRUE or FALSE',
  },
  mentors: {
    desc: 'Comma-separated list of mentors',
    mandatory: false,
    options: 'Text',
  },
};

const projectTypeMapping = {
  academic: '1',
  research: '2',
  personal: '3',
  other: '4',
};

const projectProgressStatusMapping = {
  'not started': '1',
  'in progress': '2',
  completed: '3',
};

const projectPlatformMapping = {
  competitions: '1',
  exhibitions: '2',
  industry: '3',
};

const ProjectImport = ({ user, allUsersData, onImportComplete }) => {
  // State for CSV Import
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState(false);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [initialMapping, setInitialMapping] = useState({});
  const [errorEntries, setErrorEntries] = useState([]);
  const [successfulImports, setSuccessfulImports] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const fileInputRef = useRef(null);

  // Import process steps
  const steps = ['Select CSV File', 'Map Fields', 'Import Results'];

  const handleOpenImportDialog = () => {
    setIsImportDialogOpen(true);
    setActiveStep(0);
  };

  const handleCloseImportDialog = () => {
    setIsImportDialogOpen(false);
    setCsvData({ headers: [], rows: [] });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = e => handleFileLoaded(e.target.result);
      reader.readAsText(file);
    } else {
      toast.error('Please upload a valid .csv file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileLoaded = fileContent => {
    // Normalize line endings and split into lines
    const allLines = fileContent.replace(/\r\n|\r|\n/g, '\n').split('\n');

    // Filter out empty or whitespace-only lines
    const nonEmptyLines = allLines.filter(line => line.trim() !== '');

    if (nonEmptyLines.length < 2) {
      toast.error('CSV file must contain headers and at least one data row.');
      return;
    }

    const headers = nonEmptyLines[0]
      .split(',')
      .map(header => header.trim().toLowerCase());
    const rows = nonEmptyLines
      .slice(1)
      .map(line => line.split(',').map(cell => cell.trim()));

    if (rows.length === 0) {
      toast.error('CSV file does not contain any data rows after headers.');
      return;
    }

    setCsvData({ headers, rows });

    const newInitialMapping = {};
    projectSchemaFields.forEach(field => {
      const foundHeader = headers.find(h =>
        h.replace(/\s+/g, '_').includes(field.toLowerCase())
      );
      newInitialMapping[field] = foundHeader || '';
    });

    setInitialMapping(newInitialMapping);
    setMapping(newInitialMapping);
    setIsImportDialogOpen(false);
    setIsMappingDialogOpen(true);
    setActiveStep(1); // Move to mapping step
  };

  const handleMappingSave = finalMapping => {
    setMapping(finalMapping);
    setIsMappingDialogOpen(false);
    setActiveStep(2); // Move to results step
    // Start the import process
    handleBulkImportProjects(finalMapping);
  };

  const findUserIdByEmail = email => {
    if (!email) return null;
    const foundUser = allUsersData.find(
      u => u.email && u.email.toLowerCase() === email.toLowerCase()
    );
    return foundUser ? foundUser.id : null;
  };

  const handleBulkImportProjects = async currentMapping => {
    if (!user || !user.access) {
      toast.error('Authentication token not found. Please log in again.');
      setIsLoading(false);
      return;
    }
    if (!user.user || typeof user.user.TL === 'undefined') {
      toast.error("Current user's Tinkerer's Lab ID (TL) is missing.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Starting project import...');
    setErrorEntries([]);
    setSuccessfulImports(0);
    setTotalEntries(csvData.rows.length);

    let currentSuccessCount = 0;
    const errors = [];

    for (let i = 0; i < csvData.rows.length; i++) {
      const row = csvData.rows[i];
      const entry = {};

      setLoadingMessage(`Processing row ${i + 1}/${csvData.rows.length}...`);

      try {
        projectSchemaFields.forEach(field => {
          const csvHeader = currentMapping[field];
          const headerIndex = csvData.headers.indexOf(csvHeader);
          if (
            csvHeader &&
            headerIndex !== -1 &&
            row[headerIndex] !== undefined &&
            row[headerIndex] !== ''
          ) {
            entry[field] = row[headerIndex];
          } else if (projectSchemaDescription[field]?.mandatory) {
            throw new Error(
              `Mandatory field '${projectSchemaDescription[field].desc}' is missing or not mapped for row ${i + 1}.`
            );
          }
        });

        // Data transformation and validation
        const studentEmail = entry.student_email;
        const studentId = findUserIdByEmail(studentEmail);
        if (!studentId && projectSchemaDescription.student_email.mandatory) {
          throw new Error(
            `Student email '${studentEmail}' not found or invalid for row ${i + 1}.`
          );
        }
        entry.student = studentId;

        if (entry.type) {
          const typeKey = entry.type.toLowerCase();
          if (projectTypeMapping[typeKey]) {
            entry.type = projectTypeMapping[typeKey];
          } else {
            throw new Error(
              `Invalid project type '${entry.type}' for row ${i + 1}. Valid options: ${Object.keys(projectTypeMapping).join(', ')}`
            );
          }
        }

        if (entry.progress_status) {
          const statusKey = entry.progress_status.toLowerCase();
          if (projectProgressStatusMapping[statusKey]) {
            entry.progress_status = projectProgressStatusMapping[statusKey];
          } else {
            throw new Error(
              `Invalid progress status '${entry.progress_status}' for row ${i + 1}. Valid options: ${Object.keys(projectProgressStatusMapping).join(', ')}`
            );
          }
        }

        if (entry.platform) {
          const platformKey = entry.platform.toLowerCase();
          if (projectPlatformMapping[platformKey]) {
            entry.platform = projectPlatformMapping[platformKey];
          } else {
            throw new Error(
              `Invalid platform '${entry.platform}' for row ${i + 1}. Valid options: ${Object.keys(projectPlatformMapping).join(', ')}`
            );
          }
        }

        if (typeof entry.is_patent_filed === 'string') {
          entry.is_patent_filed =
            entry.is_patent_filed.toLowerCase() === 'true';
        } else if (typeof entry.is_patent_filed === 'boolean') {
          // already boolean
        } else if (projectSchemaDescription.is_patent_filed.mandatory) {
          throw new Error(
            `'Is patent filed' must be TRUE or FALSE for row ${i + 1}.`
          );
        }

        if (entry.teammates_emails) {
          const teammateEmails = entry.teammates_emails
            .split(',')
            .map(e => e.trim());
          const teammateIds = [];
          for (const email of teammateEmails) {
            const id = findUserIdByEmail(email);
            if (id) {
              teammateIds.push(id);
            } else {
              console.warn(
                `Teammate email '${email}' not found for row ${i + 1}. Skipping this teammate.`
              );
            }
          }
          entry.teammates = teammateIds;
        } else {
          entry.teammates = [];
        }

        entry.TL = user.user.TL; // Add TL from logged-in user

        // Remove email fields as backend expects IDs
        delete entry.student_email;
        delete entry.teammates_emails;

        // Construct payload for API
        const payload = {
          title: entry.title,
          description: entry.description,
          student: entry.student,
          type: entry.type,
          progress_status: entry.progress_status,
          TL: entry.TL,
          teammates: entry.teammates || [],
          documents: entry.documents || null,
          links: entry.links || null,
          platform: entry.platform || null,
          expenditure: entry.expenditure ? parseInt(entry.expenditure) : null,
          budget: entry.budget ? parseInt(entry.budget) : null,
          duration: entry.duration ? parseFloat(entry.duration) : null,
          is_patent_filed: entry.is_patent_filed || false,
          mentors: entry.mentors || null,
        };

        // Validate numeric fields
        if (payload.expenditure && isNaN(payload.expenditure))
          throw new Error(`Expenditure must be a number for row ${i + 1}.`);
        if (payload.budget && isNaN(payload.budget))
          throw new Error(`Budget must be a number for row ${i + 1}.`);
        if (payload.duration && isNaN(payload.duration))
          throw new Error(`Duration must be a number for row ${i + 1}.`);

        await ProjectAPIs.ProjectCreate(user.access, payload);
        currentSuccessCount++;
        setSuccessfulImports(prev => prev + 1);
      } catch (err) {
        console.error('Error processing row:', i + 1, err, entry);
        errors.push({
          row: i + 1,
          data: {
            ...entry,
            student_email:
              row[csvData.headers.indexOf(currentMapping['student_email'])] ||
              'N/A',
          },
          error: err.message,
        });
      }
    }

    setErrorEntries(errors);
    setIsLoading(false);
    setLoadingMessage('');
    setIsResultDialogOpen(true);

    // Notify parent component to refresh project list after import
    if (onImportComplete && typeof onImportComplete === 'function') {
      onImportComplete();
    }
  };

  // Calculate completion status for each required field
  const getMappingCompletionStatus = () => {
    let mandatoryFieldsMapped = 0;
    let totalMandatoryFields = 0;

    Object.keys(projectSchemaDescription).forEach(field => {
      if (projectSchemaDescription[field].mandatory) {
        totalMandatoryFields++;
        if (mapping[field] && mapping[field] !== '') {
          mandatoryFieldsMapped++;
        }
      }
    });

    return {
      complete: mandatoryFieldsMapped === totalMandatoryFields,
      mandatoryFieldsMapped,
      totalMandatoryFields,
      percentage:
        totalMandatoryFields > 0
          ? (mandatoryFieldsMapped / totalMandatoryFields) * 100
          : 0,
    };
  };

  // Function to download sample CSV
  const downloadSampleCSV = () => {
    // Header row
    const headers =
      'title,description,student_email,type,progress_status,teammates_emails,documents,links,platform';
    // Sample data row
    const sampleData =
      'Smart Weather Station,IoT based weather monitoring system,john@example.com,research,in progress,jane@example.com,https://docs.example.com,https://github.com/example,competitions';

    // Combine header and data
    const csvContent = `${headers}\n${sampleData}`;

    // Create a blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary URL to the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'project_import_template.csv');
    link.style.visibility = 'hidden';

    // Append to document, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Release the blob URL
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Import Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenImportDialog}
        startIcon={<CloudUploadIcon />}
        sx={{
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontWeight: 500,
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        }}
      >
        Import Projects
      </Button>

      {/* Import Dialog with Stepper */}
      <Dialog
        open={isImportDialogOpen}
        onClose={() => {
          if (!isLoading) handleCloseImportDialog();
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Projects from CSV
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 1 }}>
              Upload a .csv file with project data
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: '#2196f3',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#edf5fe',
              },
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              Click to browse or drag & drop your CSV file here
            </Typography>
            <Typography variant="caption" color="textSecondary">
              File should contain headers and project data
            </Typography>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Paper>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={<InfoIcon />}
              label="Need a template?"
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setIsInfoDialogOpen(true)}
              sx={{ mr: 1 }}
            />
            <Chip
              icon={<DownloadIcon />}
              label="Download CSV Template"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={downloadSampleCSV}
              sx={{ mr: 1 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={handleCloseImportDialog}
            variant="outlined"
            color="inherit"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mapping Dialog */}
      <Dialog
        open={isMappingDialogOpen}
        onClose={() => !isLoading && setIsMappingDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              Map CSV Columns to Project Fields
            </Typography>
            <Chip
              label={`${getMappingCompletionStatus().mandatoryFieldsMapped}/${getMappingCompletionStatus().totalMandatoryFields} Required Fields Mapped`}
              color={
                getMappingCompletionStatus().complete ? 'success' : 'warning'
              }
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body1">
              Match your CSV column headers to the required MIS fields
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Preview: {csvData.rows.length} rows found in CSV
            </Typography>
            <Box
              sx={{
                maxHeight: '100px',
                overflow: 'auto',
                backgroundColor: '#fff',
                p: 1,
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                fontSize: '0.875rem',
              }}
            >
              <Typography
                component="div"
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
              >
                {csvData.headers.join(', ')}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              {csvData.rows.slice(0, 2).map((row, idx) => (
                <Typography
                  key={idx}
                  component="div"
                  variant="body2"
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
                >
                  {row.join(', ')}
                </Typography>
              ))}
              {csvData.rows.length > 2 && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                  ... and {csvData.rows.length - 2} more rows
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid #eee',
              maxHeight: '50vh',
              overflow: 'auto',
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 1 }}
              >
                <Grid container>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      MIS Field
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      CSV Header
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {projectSchemaFields.map(field => (
                <Grid
                  item
                  xs={12}
                  key={field}
                  sx={{
                    py: 0.5,
                    backgroundColor: projectSchemaDescription[field]?.mandatory
                      ? '#f8f8f8'
                      : 'transparent',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {projectSchemaDescription[field]?.mandatory && (
                          <Typography sx={{ color: 'error.main', mr: 0.5 }}>
                            *
                          </Typography>
                        )}
                        <Typography>
                          {projectSchemaDescription[field]?.desc || field}
                        </Typography>
                        <Tooltip
                          title={projectSchemaDescription[field]?.options || ''}
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={7}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: 'white' }}
                      >
                        <Select
                          value={mapping[field] || ''}
                          onChange={e =>
                            setMapping(prev => ({
                              ...prev,
                              [field]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{ borderRadius: '8px' }}
                          startAdornment={
                            mapping[field] ? (
                              <CheckCircleOutlineIcon
                                fontSize="small"
                                color="success"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : projectSchemaDescription[field]?.mandatory ? (
                              <ErrorOutlineIcon
                                fontSize="small"
                                color="error"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : null
                          }
                        >
                          <MenuItem value="">
                            <em>Not mapped</em>
                          </MenuItem>
                          {csvData.headers.map(header => (
                            <MenuItem
                              key={header}
                              value={header}
                              disabled={
                                Object.values(mapping).includes(header) &&
                                mapping[field] !== header
                              }
                            >
                              {header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: '1px solid #eee',
            p: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            onClick={() => {
              setIsMappingDialogOpen(false);
              setIsImportDialogOpen(true);
              setActiveStep(0);
            }}
            startIcon={<ArrowBackIcon />}
            disabled={isLoading}
          >
            Back
          </Button>
          <Box>
            <Button
              onClick={() => setIsMappingDialogOpen(false)}
              color="inherit"
              sx={{ mr: 1 }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleMappingSave(mapping)}
              variant="contained"
              color="primary"
              disabled={!getMappingCompletionStatus().complete || isLoading}
              endIcon={<ArrowForwardIcon />}
            >
              Import Projects
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog
        open={isResultDialogOpen}
        onClose={() => setIsResultDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Results
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: successfulImports > 0 ? '#f0f7f0' : '#fff',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {successfulImports > 0 ? (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 48, color: 'success.main', mr: 2 }}
                />
              ) : (
                <ErrorOutlineIcon
                  sx={{ fontSize: 48, color: 'warning.main', mr: 2 }}
                />
              )}
              <Typography variant="h5" component="div">
                {successfulImports} of {totalEntries} projects imported
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Chip
                label={`${successfulImports} Successful`}
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
              />
              {errorEntries.length > 0 && (
                <Chip
                  label={`${errorEntries.length} Failed`}
                  color="error"
                  variant="outlined"
                  icon={<ErrorOutlineIcon />}
                />
              )}
            </Box>
          </Paper>

          {errorEntries.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
              <Typography
                variant="subtitle1"
                color="error"
                fontWeight={500}
                gutterBottom
              >
                Errors ({errorEntries.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                }}
              >
                {errorEntries.map(err => (
                  <Box
                    key={err.row}
                    sx={{
                      p: 1.5,
                      borderBottom: '1px solid #eee',
                      '&:last-child': { borderBottom: 'none' },
                      backgroundColor: '#fff5f5',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Row {err.row}: {err.data.title || 'Untitled Project'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Student Email: {err.data.student_email || 'N/A'}
                      </Typography>
                    </Box>
                    <Typography
                      color="error.main"
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {err.error}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsResultDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schema Info Dialog */}
      <Dialog
        open={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Project CSV Schema Information
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              The following fields are expected for project import. Fields
              marked with an asterisk (*) are mandatory.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ mt: 2, borderRadius: '8px', overflow: 'hidden' }}
            >
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #ddd',
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Field Name
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Description
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Expected Format / Options
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {projectSchemaFields.map((field, index) => (
                  <Box
                    key={field}
                    sx={{
                      p: 1.5,
                      borderBottom:
                        index < projectSchemaFields.length - 1
                          ? '1px solid #eee'
                          : 'none',
                      backgroundColor: projectSchemaDescription[field]
                        ?.mandatory
                        ? '#f8f8f8'
                        : 'transparent',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography
                          sx={{
                            fontWeight: projectSchemaDescription[field]
                              ?.mandatory
                              ? 'bold'
                              : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {field}
                          {projectSchemaDescription[field]?.mandatory && (
                            <Box
                              component="span"
                              sx={{ color: 'error.main', ml: 0.5 }}
                            >
                              *
                            </Box>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>
                          {projectSchemaDescription[field]?.desc}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography>
                          {projectSchemaDescription[field]?.options}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#f5f9ff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Sample CSV Format
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadSampleCSV}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Download Template
                </Button>
              </Box>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  title,description,student_email,type,progress_status,teammates_emails,documents,links,platform
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  Smart Weather Station,IoT based weather monitoring
                  system,john@example.com,research,in
                  progress,jane@example.com,https://docs.example.com,https://github.com/example,competitions
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsInfoDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog
        open={isLoading}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '250px',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {loadingMessage}
          </Typography>
          {totalEntries > 0 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(successfulImports / totalEntries) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((successfulImports / totalEntries) * 100)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {successfulImports}/{totalEntries} processed
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectImport;
